import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import moment from 'moment';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {
  Field,
  FieldType,
  MemberAddeditComponent,
  MemberAddeditDialogModel,
} from '../../../brokeragency/member-addedit/member-addedit.component';
import {RewardsAdminService} from '../../../../services/rewards-admin/rewards-admin.service';
import {UiAlertService} from '../../../../services/ui-alert/ui-alert.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  parseRewardCampaignRules,
  rewardCampaignAppliesToAllBrands,
} from '../../../../helpers/reward-campaign-rules.helper';

@Component({
    selector: 'app-superadmin-rewards-campaigns',
    templateUrl: './superadmin-rewards-campaigns.component.html',
    styleUrls: ['./superadmin-rewards-campaigns.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SuperadminRewardsCampaignsComponent implements OnInit, OnDestroy {
  public displayedColumns: string[] = [
    'campaignName',
    'brand',
    'startAt',
    'endAt',
    'startingBalance',
    'currentBalance',
    'lowBalance',
    'actions',
  ];
  public items: any[] = [];
  public searchValue = '';
  public page = 0;
  public limit = 30;
  public endOfList = false;
  public loading = false;
  public brands: any[] = [];
  public moment = moment;
  public readonly allBrandsCampaignTooltip = 'No brands are defined in Rules JSON. This campaign applies to all brands.';
  private readonly searchDebounceMs = 300;
  private readonly searchValueChanges = new Subject<string>();
  private readonly searchSubscription: Subscription;
  private rewards: any[] = [];
  private readonly addEditFields: Array<Field> = [
    {name: 'campaignName', label: 'Campaign Name', fieldType: FieldType.F_STRING, required: true},
    {name: 'brandId', label: 'Brand', fieldType: FieldType.F_DROPDOWN, required: true, values: []},
    {name: 'startAt', label: 'Start Date', fieldType: FieldType.F_ANYDATE, required: true},
    {name: 'endAt', label: 'End Date', fieldType: FieldType.F_ANYDATE, required: true},
    {name: 'startingBalance', label: 'Starting Balance', fieldType: FieldType.F_CURRENCY, required: true},
    {name: 'currentBalance', label: 'Current Balance', fieldType: FieldType.F_CURRENCY, required: true},
    {name: 'lowBalance', label: 'Low Balance', fieldType: FieldType.F_CURRENCY, required: true},
    {
      name: 'rules',
      label: 'Rules JSON',
      fieldType: FieldType.F_JSON,
      required: false,
      hint: 'Stored as JSON. Edit the full object here.',
    },
    {
      name: 'rewardIds',
      label: 'Rewards',
      fieldType: FieldType.F_DROPDOWN,
      multiple: true,
      required: false,
      values: [],
      hint: 'Select rewards to associate with this campaign.',
    },
  ];

  constructor(
    private rewardsAdminService: RewardsAdminService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private uiAlertService: UiAlertService,
  ) {
    this.searchSubscription = this.searchValueChanges
      .pipe(
        debounceTime(this.searchDebounceMs),
        distinctUntilChanged(),
      )
      .subscribe(() => this.loadItems(true));
  }

  ngOnInit(): void {
    this.loadBrands();
    this.loadRewards();
    this.loadItems(true);
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  async loadBrands(): Promise<void> {
    try {
      this.brands = await this.rewardsAdminService.getBrands();
      this.updateDialogOptions();
    } catch (error) {
      console.error('Error loading brands', error);
    }
  }

  async loadRewards(): Promise<void> {
    try {
      const rewards = await this.rewardsAdminService.getRewards('', 1, 10000);
      this.rewards = (rewards || []).map((reward: any) => this.normalizeRewardRecord(reward));
      this.updateDialogOptions();
    } catch (error) {
      console.error('Error loading rewards', error);
    }
  }

  getBrandName(campaign: any): string {
    const brand = this.brands?.find((b: any) => String(b.id) === String(campaign?.brandId || ''));
    return brand ? brand.name : '';
  }

  async loadItems(clear = false): Promise<void> {
    if (clear) {
      this.page = 0;
      this.endOfList = false;
      this.items = [];
    }
    if (this.loading || this.endOfList) {
      return;
    }
    this.loading = true;
    try {
      const campaigns = await this.rewardsAdminService.getCampaigns(this.searchValue, this.page, this.limit);
      if (!campaigns || campaigns.length === 0) {
        this.endOfList = true;
      } else {
        this.items = this.items.concat((campaigns || []).map((item: any) => this.normalizeCampaignRecord(item)));
        this.page += 1;
      }
    } catch (error) {
      console.error('Error loading campaigns', error);
    } finally {
      this.loading = false;
    }
  }

  queueFilter(value: string): void {
    this.searchValueChanges.next((value || '').toString());
  }

  campaignAppliesToAllBrands(campaign: any): boolean {
    return rewardCampaignAppliesToAllBrands(campaign?.rules);
  }

  async addCampaign(): Promise<void> {
    await this.ensureDialogOptionsLoaded();
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(
        this.toDialogCampaignData(null, []),
        this.getDialogFields(),
        'Campaign',
        true,
        'Add',
      ),
    });
    dialogRef.afterClosed().subscribe(async (dialogResult) => {
      if (!dialogResult) {
        return;
      }

      const payload = this.buildCampaignPayload(dialogResult);
      if (!payload) {
        return;
      }

      if (!await this.confirmAllBrandsCampaign(payload)) {
        return;
      }

      this.rewardsAdminService.postCampaign(payload).then(async (result) => {
        const campaignId = this.getCampaignId(result);
        await this.saveCampaignRewards(campaignId, [], this.getDialogRewardIds(dialogResult));
        this.loadItems(true);
        this.snackBar.open('Campaign successfully created', 'Close', {duration: 2000});
      }).catch((error) => {
        console.error('Error creating campaign', error);
        this.snackBar.open(this.getErrorMessage(error, 'Unable to create campaign'), 'Close', {duration: 3000});
      });
    });
  }

  async editCampaign(campaign: any): Promise<void> {
    await this.ensureDialogOptionsLoaded();
    const campaignId = this.getCampaignId(campaign);
    if (!campaignId) {
      return;
    }

    try {
      const fullCampaign = this.normalizeCampaignRecord(await this.rewardsAdminService.getCampaignById(campaignId));
      const dialogCampaign = this.mergeCampaignRecords(campaign, fullCampaign);
      const currentRewardIds = await this.loadCampaignRewardIds(campaignId);
      const dialogRef = this.dialog.open(MemberAddeditComponent, {
        data: new MemberAddeditDialogModel(
          this.toDialogCampaignData(dialogCampaign, currentRewardIds),
          this.getDialogFields(),
          `Campaign "${dialogCampaign?.campaignName || ''}"`,
          true,
          'Edit',
        ),
      });
      dialogRef.afterClosed().subscribe(async (dialogResult) => {
        if (!dialogResult) {
          return;
        }

        const payload = this.buildCampaignPayload(dialogResult, dialogCampaign);
        if (!payload) {
          return;
        }

        if (!await this.confirmAllBrandsCampaign(payload)) {
          return;
        }

        this.rewardsAdminService.putCampaign(payload).then(async () => {
          await this.saveCampaignRewards(campaignId, currentRewardIds, this.getDialogRewardIds(dialogResult));
          this.loadItems(true);
          this.snackBar.open('Campaign successfully updated', 'Close', {duration: 2000});
        }).catch((error) => {
          console.error('Error updating campaign', error);
          this.snackBar.open(this.getErrorMessage(error, 'Unable to update campaign'), 'Close', {duration: 3000});
        });
      });
    } catch (error) {
      console.error('Error loading campaign', error);
      this.snackBar.open(this.getErrorMessage(error, 'Unable to load campaign details'), 'Close', {duration: 3000});
    }
  }

  async deleteCampaign(event: Event, campaign: any): Promise<void> {
    event.stopPropagation();
    const campaignId = this.getCampaignId(campaign);
    if (!campaignId) {
      return;
    }

    const confirm = await this.uiAlertService.presentAlertConfirm('Delete this campaign?');
    if (!confirm) {
      return;
    }

    try {
      await this.rewardsAdminService.deleteCampaign({id: campaignId});
      this.loadItems(true);
      this.snackBar.open('Campaign successfully deleted', 'Close', {duration: 2000});
    } catch (error) {
      console.error('Error deleting campaign', error);
      this.snackBar.open('Unable to delete campaign', 'Close', {duration: 3000});
    }
  }

  onScroll(event: any): void {
    const scrollTop = event.target.scrollTop;
    const scrollHeight = event.target.scrollHeight;
    const offsetHeight = event.target.offsetHeight;
    const threshold = 150;
    if (scrollTop + offsetHeight >= scrollHeight - threshold) {
      this.loadItems();
    }
  }

  private async ensureDialogOptionsLoaded(): Promise<void> {
    const loaders: Array<Promise<void>> = [];
    if (!this.brands.length) {
      loaders.push(this.loadBrands());
    }
    if (!this.rewards.length) {
      loaders.push(this.loadRewards());
    }
    await Promise.all(loaders);
  }

  private getDialogFields(): Array<Field> {
    return this.addEditFields.map((field) => ({
      ...field,
      values: this.getFieldValues(field.name, field.values),
    }));
  }

  private updateDialogOptions(): void {
    this.addEditFields.forEach((field) => {
      field.values = this.getFieldValues(field.name, field.values);
    });
  }

  private getFieldValues(fieldName: string, existingValues?: string[]): string[] {
    if (fieldName === 'brandId') {
      return this.getBrandDropdownValues();
    }

    if (fieldName === 'rewardIds') {
      return this.getRewardDropdownValues();
    }

    return existingValues || [];
  }

  private getBrandDropdownValues(): Array<string> {
    return (this.brands || [])
      .slice()
      .sort((brandA: any, brandB: any) => String(brandA?.name || '').localeCompare(String(brandB?.name || '')))
      .map((brand: any) => `${this.formatDropdownLabel(brand.name)}:${brand.id}`);
  }

  private getRewardDropdownValues(): Array<string> {
    return (this.rewards || [])
      .slice()
      .sort((rewardA: any, rewardB: any) => this.getRewardLabel(rewardA).localeCompare(this.getRewardLabel(rewardB)))
      .map((reward: any) => `${this.formatDropdownLabel(this.getRewardLabel(reward))}:${this.getRewardId(reward)}`);
  }

  private toDialogCampaignData(campaign: any, rewardIds: string[]): Record<string, any> {
    return {
      id: this.getCampaignId(campaign),
      campaignName: campaign?.campaignName || '',
      brandId: campaign?.brandId || '',
      startAt: this.toDialogDate(campaign?.startAt),
      endAt: this.toDialogDate(campaign?.endAt),
      startingBalance: this.toDialogAmount(campaign?.startingBalance),
      currentBalance: this.toDialogAmount(campaign?.currentBalance),
      lowBalance: this.toDialogAmount(campaign?.lowBalance),
      rules: this.parseJsonValue(campaign?.rules),
      rewardIds,
    };
  }

  private buildCampaignPayload(dialogResult: Record<string, any>, currentCampaign?: any): Record<string, any> | null {
    const campaignName = String(dialogResult?.campaignName || '').trim();
    const brandId = String(dialogResult?.brandId || '').trim();
    const startAt = this.normalizeDate(dialogResult?.startAt);
    const endAt = this.normalizeDate(dialogResult?.endAt);
    const startingBalance = String(dialogResult?.startingBalance ?? '').trim();
    const currentBalance = String(dialogResult?.currentBalance ?? '').trim();
    const lowBalance = String(dialogResult?.lowBalance ?? '').trim();

    if (!campaignName || !brandId || !startAt || !endAt) {
      this.snackBar.open('Campaign name, brand, start date, and end date are required', 'Close', {duration: 3000});
      return null;
    }

    if (!startingBalance || !currentBalance || !lowBalance) {
      this.snackBar.open('Starting balance, current balance, and low balance are required', 'Close', {duration: 3000});
      return null;
    }

    const payload: Record<string, any> = {
      campaignName,
      brandId,
      startAt,
      endAt,
      startingBalance,
      currentBalance,
      lowBalance,
      rules: this.parseJsonValue(dialogResult?.rules),
    };

    const campaignId = this.getCampaignId(currentCampaign);
    if (campaignId) {
      payload.id = campaignId;
    }

    return payload;
  }

  private async loadCampaignRewardIds(campaignId: string): Promise<string[]> {
    const rewards = await this.rewardsAdminService.getRewardsForCampaign(campaignId);
    const campaignRewards = (rewards || []).map((reward: any) => this.normalizeRewardRecord(reward));
    this.mergeMissingRewards(campaignRewards);
    return campaignRewards
      .map((reward: any) => this.getRewardId(reward))
      .filter(Boolean);
  }

  private async saveCampaignRewards(campaignId: string, initialRewardIds: string[], selectedRewardIds: string[]): Promise<void> {
    if (!campaignId) {
      if (selectedRewardIds.length) {
        throw new Error('Campaign was saved, but no campaign id was returned for reward associations.');
      }
      return;
    }

    const selectedRewardIdSet = new Set(selectedRewardIds);
    const initialRewardIdSet = new Set(initialRewardIds);
    const changedRewardIds = Array.from(new Set([
      ...selectedRewardIds,
      ...initialRewardIds,
    ])).filter((rewardId) => selectedRewardIdSet.has(rewardId) !== initialRewardIdSet.has(rewardId));

    await Promise.all(changedRewardIds.map(async (rewardId) => {
      const campaignIds = await this.getCampaignIdsForReward(rewardId);
      const nextCampaignIds = selectedRewardIdSet.has(rewardId)
        ? Array.from(new Set([...campaignIds, campaignId]))
        : campaignIds.filter((id) => id !== campaignId);
      await this.rewardsAdminService.putCampaignsForReward(rewardId, nextCampaignIds);
    }));
  }

  private async getCampaignIdsForReward(rewardId: string): Promise<string[]> {
    const campaigns = await this.rewardsAdminService.getCampaignsForReward(rewardId);
    return (campaigns || [])
      .map((campaign: any) => this.getCampaignId(campaign))
      .filter(Boolean);
  }

  private getDialogRewardIds(dialogResult: Record<string, any>): string[] {
    return (dialogResult?.rewardIds || [])
      .map((rewardId: any) => String(rewardId || '').trim())
      .filter(Boolean);
  }

  private parseJsonValue(value: any): any {
    return parseRewardCampaignRules(value);
  }

  private getCampaignId(campaign: any): string {
    return String(campaign?.id || campaign?.campaignId || campaign?._id || '').trim();
  }

  private getRewardId(reward: any): string {
    return String(reward?.id || reward?.rewardId || reward?._id || '').trim();
  }

  private getRewardLabel(reward: any): string {
    const rewardName = String(reward?.name || '').trim();
    const bannerDetails = this.parseJsonValue(reward?.bannerDetails);
    const title = String(bannerDetails?.title || '').trim();
    return title && title !== rewardName ? `${rewardName} - ${title}` : rewardName;
  }

  private confirmAllBrandsCampaign(campaign: any): Promise<boolean> {
    if (!rewardCampaignAppliesToAllBrands(campaign?.rules)) {
      return Promise.resolve(true);
    }

    return this.uiAlertService.presentAlertConfirm(
      'No brands are defined in the Rules JSON, so this campaign will apply to all brands. Are you sure you want to do this?',
    );
  }

  private normalizeCampaignRecord(campaign: any): any {
    if (!campaign) {
      return campaign;
    }

    const campaignId = this.getCampaignId(campaign);
    return campaignId ? {...campaign, id: campaignId} : campaign;
  }

  private normalizeRewardRecord(reward: any): any {
    if (!reward) {
      return reward;
    }

    const rewardId = this.getRewardId(reward);
    return rewardId ? {...reward, id: rewardId} : reward;
  }

  private mergeMissingRewards(rewards: any[]): void {
    const existingRewardIds = new Set(this.rewards.map((reward: any) => this.getRewardId(reward)));
    const missingRewards = (rewards || []).filter((reward: any) => !existingRewardIds.has(this.getRewardId(reward)));
    if (!missingRewards.length) {
      return;
    }

    this.rewards = this.rewards.concat(missingRewards);
    this.updateDialogOptions();
  }

  private mergeCampaignRecords(baseCampaign: any, detailedCampaign: any): any {
    const base = this.normalizeCampaignRecord(baseCampaign) || {};
    const detailed = this.normalizeCampaignRecord(detailedCampaign) || {};

    return {
      ...base,
      ...detailed,
      id: this.getCampaignId(detailed) || this.getCampaignId(base),
      campaignName: detailed?.campaignName ?? base?.campaignName ?? '',
      brandId: detailed?.brandId ?? base?.brandId ?? '',
      startAt: detailed?.startAt ?? base?.startAt ?? null,
      endAt: detailed?.endAt ?? base?.endAt ?? null,
      startingBalance: detailed?.startingBalance ?? base?.startingBalance ?? '',
      currentBalance: detailed?.currentBalance ?? base?.currentBalance ?? '',
      lowBalance: detailed?.lowBalance ?? base?.lowBalance ?? '',
      rules: detailed?.rules ?? base?.rules ?? {},
    };
  }

  private normalizeDate(value: any): string | null {
    if (!value) {
      return null;
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date.toISOString();
  }

  private toDialogDate(value: any): Date | null {
    if (!value) {
      return null;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private toDialogAmount(value: any): string {
    return value === null || value === undefined ? '' : String(value);
  }

  private formatDropdownLabel(value: any): string {
    return String(value || '').replace(/:/g, ' -');
  }

  private getErrorMessage(error: any, fallback: string): string {
    return error?.error?.error
      || error?.error?.message
      || error?.data?.error
      || error?.data?.message
      || error?.message
      || fallback;
  }
}
